/**
 * Global Job Description listing page.
 * Route: /ats/job-descriptions
 *
 * Shows all JDs across all jobs in the workspace.
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  Eye,
  RefreshCw,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataTable,
  useAuth,
  toast,
  SearchInput,
  MultiSelectCombobox,
} from '@aaas/shared';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useAllJDListQuery } from '../../hooks/useJobDescriptionQueries';
import { useATSRole } from '../../hooks/useATSRole';
import { JDUploadDialog } from '../../components/JDUploadDialog';
import jobDescriptionService from '../../services/job-description.service';
import type { JobDescription, JDProcessingStatus } from '../../types';
import { JD_PROCESSING_STATUS_CONFIG } from '../../types';
import { ProcessingStatusBadge } from '../../components/jd/ProcessingStatusBadge';

export function JDListingPage() {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation(['ats', 'common']);

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingJD, setDeletingJD] = useState<JobDescription | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [searchKey, setSearchKey] = useState(0);

  const { data: jdResponse, isLoading, isFetching, error, refetch } = useAllJDListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace,
    q: searchQuery.trim() || undefined,
    statuses: selectedStatuses.length ? selectedStatuses : undefined,
    jobIds: selectedJobIds.length ? selectedJobIds : undefined,
  });

  const jds = jdResponse?.data?.items ?? [];

  // Derive job options from current results (updates as data loads)
  const jobOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const jd of jds) {
      if (!seen.has(jd.job_id)) seen.set(jd.job_id, jd.job_name ?? jd.job_id);
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ value: id, label }));
  }, [jds]);

  const statusOptions = useMemo(() =>
    Object.entries(JD_PROCESSING_STATUS_CONFIG).map(([value, cfg]) => ({
      value,
      label: cfg.label,
    })),
  []);

  const hasActiveFilters = searchQuery.trim() !== '' || selectedStatuses.length > 0 || selectedJobIds.length > 0;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatuses([]);
    setSelectedJobIds([]);
    setSearchKey((k) => k + 1);
  };

  const handleViewDetail = useCallback((jd: JobDescription) => {
    navigate(`/ats/job-descriptions/${jd.id}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((jd: JobDescription) => {
    setDeletingJD(jd);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingJD || !user || !currentWorkspace) return;
    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds(prev => new Set(prev).add(deletingJD.id));
    try {
      const token = await user.getIdToken();
      await jobDescriptionService.deleteJobDescription(
        currentWorkspace.id,
        token,
        deletingJD.job_id,
        deletingJD.id,
      );
      toast.success(t('jd.toast.deleted'));
      await refetch();
      setShowDeleteDialog(false);
      setDeletingJD(null);
    } catch {
      setDeleteError(t('jd.toast.deleteFailed'));
    } finally {
      setIsDeleting(false);
      setDeletingIds(prev => {
        const next = new Set(prev);
        if (deletingJD) next.delete(deletingJD.id);
        return next;
      });
    }
  };

  const columns = useMemo<DataTableColumn<JobDescription>[]>(() => [
    {
      key: 'name',
      title: t('jd.columns.name'),
      render: (value: string, record: JobDescription) => (
        <button
          onClick={() => handleViewDetail(record)}
          className="font-medium text-orange-600 hover:text-orange-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'job_name',
      title: t('jd.columns.job'),
      width: 200,
      render: (value: string | null, record: JobDescription) => (
        <button
          onClick={() => navigate(`/ats/jobs/${record.job_id}`)}
          className="text-sm text-gray-600 hover:text-orange-600 hover:underline text-left truncate max-w-[180px]"
          title={value ?? undefined}
        >
          {value ?? record.job_id}
        </button>
      ),
    },
    {
      key: 'file_name',
      title: t('jd.columns.file'),
      width: 180,
      render: (value: string | null, record: JobDescription) => (
        value ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <FileText className="h-3 w-3 flex-shrink-0" />
            <span className="truncate max-w-[120px]" title={value}>{value}</span>
            {record.file_type && (
              <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase flex-shrink-0">
                {record.file_type}
              </span>
            )}
          </span>
        ) : record.raw_content ? (
          <span className="text-xs text-gray-400">{t('jd.pastedText')}</span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )
      ),
    },
    {
      key: 'processing_status',
      title: t('jd.columns.aiStatus'),
      width: 130,
      render: (value: JDProcessingStatus) => <ProcessingStatusBadge status={value} />,
    },
    {
      key: 'updated_at',
      title: t('jd.columns.updated'),
      width: 110,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ], [handleViewDetail, navigate, t]);

  const actions = useMemo<DataTableAction<JobDescription>[]>(() => {
    const base: DataTableAction<JobDescription>[] = [
      {
        key: 'view',
        label: t('jd.viewDetail'),
        icon: <Eye className="h-4 w-4" />,
        onClick: handleViewDetail,
        inline: true,
      },
    ];
    if (isLeaderOrAbove) {
      base.push({
        key: 'delete',
        label: t('jd.delete'),
        icon: <Trash2 className="h-4 w-4" />,
        loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
        onClick: handleDeleteClick,
        loading: (record) => deletingIds.has(record.id),
        destructive: true,
        inline: true,
      });
    }
    return base;
  }, [isLeaderOrAbove, handleViewDetail, handleDeleteClick, deletingIds, t]);

  if (isLoading && !jdResponse) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('jd.pageTitle')}</h1>
            <p className="text-sm text-gray-500">{t('jd.pageDescription')}</p>
          </div>
          {isFetching && !isLoading && <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('common:action.retry')}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {isLeaderOrAbove && (
            <button
              onClick={() => setShowUploadDialog(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('jd.uploadJd')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('jd.loadFailed', { defaultValue: 'Failed to load job descriptions' })}
          </AlertDescription>
        </Alert>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          key={searchKey}
          initialValue=""
          onSearch={setSearchQuery}
          placeholder={t('jd.searchPlaceholder')}
          className="w-64"
        />
        <MultiSelectCombobox
          options={statusOptions}
          values={selectedStatuses}
          onChange={setSelectedStatuses}
          placeholder={t('job.columns.status')}
          className="w-40"
        />
        <MultiSelectCombobox
          options={jobOptions}
          values={selectedJobIds}
          onChange={setSelectedJobIds}
          placeholder={t('nav.jobs')}
          className="w-48"
        />
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="h-3 w-3" />
            {t('common:action.clear', { defaultValue: 'Clear' })}
          </button>
        )}
        {hasActiveFilters && (
          <span className="text-xs text-gray-400 ml-1">
            {t('jd.resultCount', { count: jds.length })}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        data={jds}
        loading={isLoading && !jdResponse}
        actions={actions}
        rowKey={(record) => record.id}
        emptyMessage={t('jd.empty')}
      />

      <JDUploadDialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onSuccess={() => { refetch(); setShowUploadDialog(false); }}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => { if (!isDeleting) { setShowDeleteDialog(open); if (!open) setDeletingJD(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('jd.deleteTitle', { defaultValue: 'Delete Job Description' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('jd.deleteBody', {
                defaultValue: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
                name: deletingJD?.name,
              })}
            </AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('common:action.cancel')}</AlertDialogCancel>
            <Button onClick={(e) => { e.preventDefault(); handleDeleteConfirm(); }} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {isDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common:state.loading')}</> : t('common:action.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
